/**
 * useIsMobile — single source of truth for "am I on a mobile viewport?"
 *
 * Uses `matchMedia` with a listener so components re-render when the user
 * rotates or resizes the browser window. Server-side / initial render is
 * safe: on the first paint we fall back to `false` (desktop) so we never
 * flash the wrong layout during hydration in the K8s preview.
 *
 * A single breakpoint (`767px`) mirrors Tailwind's `md:` prefix, which is
 * what every Batch 1 primitive uses to switch presentation.
 */
import { useEffect, useState } from "react";

// Aligned to Tailwind's `lg:` prefix — every Batch 1 primitive switches
// to its mobile/tablet variant below 1024 px and to its desktop layout
// at or above 1024 px. Tablets (≥ 768 < 1024) intentionally get the
// mobile drawer + compact header because the desktop TopBar's controls
// only fit on wider viewports.
const MOBILE_BREAKPOINT_PX = 1023;
const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT_PX}px)`;

export default function useIsMobile() {
  const getInitial = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_QUERY).matches;
  };
  const [isMobile, setIsMobile] = useState(getInitial);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    // Safari < 14 uses addListener, everyone else uses addEventListener.
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    setIsMobile(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  return isMobile;
}

export { MOBILE_BREAKPOINT_PX, MOBILE_QUERY };
